import { Tag } from "lucide-react";
import CarCard from "./CarCard";
import { useLanguage } from "../contexts/LanguageContext";

const formatOfferDate = (value) => {
  if (!value) return null;
  return new Date(value).toLocaleDateString();
};

export const OffersShowcaseSection = ({
  offers = [],
  className = "py-12 md:py-24",
  title,
  subtitle,
  emptyState = null,
  testId = "offers-showcase-section",
}) => {
  const { t } = useLanguage();
  const visibleOffers = offers.filter((offer) => offer?.enabled !== false);

  const offerPeriodLabel = (period) => {
    if (period === "week") return t("common.weekly", "Weekly");
    if (period === "month") return t("common.monthly", "Monthly");
    return t("common.day", "Day");
  };

  const getOfferPeriods = (offer) => (
    Object.entries(offer.periods || {})
      .filter(([, config]) => config?.enabled)
      .map(([period]) => period)
  );

  if (!visibleOffers.length) {
    return emptyState;
  }

  return (
    <section
      className={`relative overflow-hidden bg-gradient-to-b from-amber-50 to-white dark:from-gray-900 dark:to-gray-950 ${className}`.trim()}
      data-testid={testId}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(249,115,22,0.18),_transparent_24%),radial-gradient(circle_at_bottom_left,_rgba(212,175,55,0.14),_transparent_28%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center">
              <Tag className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-orange-500 text-xs uppercase tracking-[0.2em] font-semibold">
                {subtitle || t("sections.limitedTime", "Limited Time")}
              </span>
              <h2 className="font-[var(--font-display)] text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {title || t("sections.latestOffers", "Latest Offers")}
              </h2>
            </div>
          </div>
        </div>
        <div className="space-y-8">
          {visibleOffers.map((offer) => {
            const periods = getOfferPeriods(offer);

            return (
              <div
                key={offer.id}
                className="rounded-[2rem] border border-orange-100 bg-white/90 p-6 shadow-sm backdrop-blur dark:border-orange-500/10 dark:bg-gray-900/70"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="font-[var(--font-display)] text-2xl font-semibold text-gray-900 dark:text-white">
                      {offer.title}
                    </h3>
                    {!!offer.partners?.length && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {offer.partners.map((partner) => partner.name).join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-[0.16em]">
                    {periods.map((period) => (
                      <span
                        key={period}
                        className="rounded-full bg-orange-100 px-3 py-1 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300"
                      >
                        {offerPeriodLabel(period)}
                      </span>
                    ))}
                    {offer.startAt && offer.endAt && (
                      <span className="rounded-full bg-black/5 px-3 py-1 text-gray-600 dark:bg-white/10 dark:text-gray-300">
                        {formatOfferDate(offer.startAt)} - {formatOfferDate(offer.endAt)}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                  {offer.cars.map((car) => (
                    <CarCard key={`${offer.id}-${car.id}`} car={car} badge="offer" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OffersShowcaseSection;
